import { isMapId, mapInfo, type MapId } from "@69ly/maps";
import { sameThrow, type CopyStuffsResult, type Scope, type Stuff } from "@69ly/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../components/Icon.tsx";
import { Link, Navigate, useParams } from "react-router";
import { useDestinations } from "../components/CopyTo.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { useCallouts } from "../lib/callouts.ts";
import { PERSONAL, useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useCopyStuffs, useStuffs } from "../lib/stuff-queries.ts";

/**
 * Copies stuffs of one map from another library (personal, another team) into the
 * library being looked at. What this library already has is marked and left out.
 */
function ImportView({ map }: { map: MapId }) {
  const { t } = useTranslation();
  const { scope, team } = useCurrentTeam();
  const sources = useDestinations(scope);
  const [source, setSource] = useState<Scope | null>(null);
  const from = source && sources.some((s) => s.scope === source) ? source : (sources[0]?.scope ?? null);
  const here = useStuffs(scope, map);
  const there = useStuffs(from ?? PERSONAL, map);
  const copy = useCopyStuffs();
  const callouts = useCallouts();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<(CopyStuffsResult & { from: string }) | null>(null);
  const hereName = team?.name ?? t("nav.personal");
  const info = mapInfo(map);

  const rows = useMemo(
    () => (there.data ?? []).map((s) => ({ stuff: s, already: (here.data ?? []).some((h) => sameThrow(h, s)) })),
    [there.data, here.data],
  );
  const fresh = rows.filter((r) => !r.already);
  const chosen = rows.filter((r) => picked.has(r.stuff.id) && !r.already);

  if (sources.length === 0)
    return (
      <div className="page page-narrow">
        <div className="card">
          <h1 className="section-title">{t("import.title", { map: info.name, library: hereName })}</h1>
          <p className="muted">{t("import.noSource")}</p>
        </div>
      </div>
    );

  const toggle = (s: Stuff) => {
    const next = new Set(picked);
    if (next.has(s.id)) next.delete(s.id);
    else next.add(s.id);
    setPicked(next);
  };
  const run = () =>
    copy.mutate(
      { ids: chosen.map((r) => r.stuff.id), to: scope },
      {
        onSuccess: (r) => {
          setResult({ ...r, from: sources.find((x) => x.scope === from)?.name ?? "" });
          setPicked(new Set());
        },
      },
    );

  return (
    <div className="page page-narrow import-page">
      <div>
        <Link to={`/library/${map}`} className="back-link">
          <Icon name="chevronLeft" size={16} />{t("detail.back", { map: info.name })}
        </Link>
        <h1 className="page-title">{t("import.title", { map: info.name, library: hereName })}</h1>
        <p className="muted">{t("import.intro")}</p>
      </div>

      {result && (
        <Banner tone="success">
          {t("import.done", { count: result.copied.length, library: hereName })}
          {result.skipped.length > 0 && ` ${t("import.skipped", { count: result.skipped.length })}`}
          {result.missingFiles > 0 && ` ${t("import.missingFiles", { count: result.missingFiles })}`}{" "}
          <Link to={`/library/${map}`}>{t("import.see")}</Link>
        </Banner>
      )}

      <div className="card import-card">
        <label className="field import-source">
          <span>{t("import.from")}</span>
          <select
            value={from ?? ""}
            onChange={(e) => {
              setSource(e.target.value);
              setPicked(new Set());
            }}
          >
            {sources.map((s) => (
              <option key={s.scope} value={s.scope}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        {there.isPending || here.isPending ? (
          <Loading />
        ) : there.error ? (
          <ErrorState message={errorText(t, there.error)} onRetry={() => void there.refetch()} />
        ) : rows.length === 0 ? (
          <p className="muted">{t("import.empty", { map: info.name })}</p>
        ) : (
          <>
            <div className="row import-bar">
              <span className="muted small">{t("import.counts", { count: rows.length, fresh: fresh.length })}</span>
              <button
                type="button"
                className="link-button small"
                disabled={fresh.length === 0}
                onClick={() => setPicked(chosen.length === fresh.length ? new Set() : new Set(fresh.map((r) => r.stuff.id)))}
              >
                {chosen.length === fresh.length && fresh.length > 0 ? t("import.selectNone") : t("import.selectAll")}
              </button>
            </div>
            <ul className="import-list">
              {rows.map(({ stuff: s, already }) => {
                const { from: fromPlace, to } = callouts.places(map, s);
                return (
                  <li key={s.id}>
                    <label className={`import-row${already ? " is-already" : ""}`}>
                      <input type="checkbox" checked={!already && picked.has(s.id)} disabled={already} onChange={() => toggle(s)} />
                      <GrenadeGlyph grenade={s.grenade} size={16} />
                      <span className="import-name">
                        <b>{s.title}</b>
                        <span className="muted small">
                          {[t(`sideShort.${s.side}`), fromPlace && to ? t("places.fromTo", { from: fromPlace, to }) : (to ?? fromPlace), s.media.length ? t("import.pictures", { count: s.media.length }) : null, s.clip ? t("import.video") : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      {already && <span className="import-tag">{t("import.already")}</span>}
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <div className="import-actions">
        {copy.error && <p className="field-error small">{errorText(t, copy.error)}</p>}
        <button type="button" className="btn btn-primary" disabled={chosen.length === 0 || copy.isPending} onClick={run}>
          {copy.isPending ? t("copy.copying") : t("import.copy", { count: chosen.length, library: hereName })}
        </button>
      </div>
    </div>
  );
}

export function ImportStuffsPage() {
  const { map } = useParams();
  if (!isMapId(map)) return <Navigate to="/library" replace />;
  return <ImportView map={map} />;
}
